class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = null;
    this.requestId = 0;
    this.liveRegionTimer = null;
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Mirror the parent SearchForm guard: skip binding when there's no input
    // (and therefore no form) to bind to.
    if (this.input) {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      // SearchForm debounces onChange by 300ms. Invalidate an older request on
      // the raw input event so it cannot render during that debounce window;
      // the replacement network request remains debounced.
      this.input.addEventListener('input', this.onInput.bind(this));
    }
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onInput() {
    if (this.hasAttribute('data-results-section')) {
      this.storybookDismissed = false;
      // Options on screen belong to the query that fetched them: a new query drops the old selection at once.
      if (this.getQuery() !== this.storybookRenderedTerm) this.clearStorybookSelection();
    }
    if (this.getQuery() === this.searchTerm) return;
    this.cancelPendingSearch();
    this.removeAttribute('loading');
    this.hideSearchError();
  }

  onChange() {
    if (this.hasAttribute('data-results-section') && (this.storybookDismissed || !this.isConnected)) return;
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.cancelPendingSearch();
      this.hideSearchError();
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (this.hasAttribute('data-results-section')) {
      if (!this.getQuery().length) event.preventDefault();
      return;
    }
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    if (this.hasAttribute('data-results-section')) {
      event.preventDefault();
      this.input.value = '';
      this.searchTerm = '';
      this.close(true);
      this.input.focus();
      this.storybookDismissed = true;
      this.toggleResetButton();
      this.input.dispatchEvent(new Event('search-storybook:reset', { bubbles: true }));
      return;
    }
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.cancelPendingSearch();
      this.hideSearchError();
      this.closeResults(true);
    }
  }

  cancelPendingSearch() {
    this.requestId += 1;
    if (this.abortController) this.abortController.abort();
    this.abortController = null;
  }

  onFocus() {
    if (this.hasAttribute('data-results-section')) this.storybookDismissed = false;
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (this.hasAttribute('data-results-section')) return;
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (this.hasAttribute('data-results-section')) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.switchStorybookOption(event.key === 'ArrowUp' ? -1 : 1);
      } else if (event.key === 'Enter' && event.target === this.input) {
        const selected = this.querySelector('[role="option"][aria-selected="true"]');
        if (selected && this.getQuery() === this.storybookRenderedTerm) { event.preventDefault(); selected.click(); }
      } else if ((event.key === ' ' || event.key === 'Enter') && event.target.matches('button[data-ss-suggestion]')) {
        event.preventDefault();
        event.target.click();
      }
      return;
    }
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      // Escape regex metacharacters so a term containing e.g. "(" or "*" can't
      // throw on `new RegExp(...)` (an unbalanced "(" is a SyntaxError) or
      // mis-match, and guard the possibly-null `.match()` result before reading
      // `.length` (no match returns null, not []). Both cases were crashing
      // predictive search as users typed. `.replace` below still uses the raw
      // term (a literal string replace), so behaviour is otherwise unchanged.
      const safeTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      if ((currentButtonText.match(new RegExp(safeTerm, 'g')) || []).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    if (this.hasAttribute('data-results-section')) return this.getStorybookResults(searchTerm);
    const queryKey = searchTerm.trim().toLowerCase();
    this.cancelPendingSearch();
    const requestId = this.requestId;
    const isCurrentRequest = () => requestId === this.requestId && searchTerm === this.searchTerm;
    this.hideSearchError();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      if (isCurrentRequest()) this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const abortController = new AbortController();
    this.abortController = abortController;

    // FULL search engine, not the predictive endpoint (owner 2026-07-26).
    // Predictive only matches product titles/vendor/type, so a term living in
    // tags or a description — "halloween" — returned nothing here while the
    // results page found nine products. The Section Rendering API renders
    // sections/predictive-search.liquid against the full `search` object on
    // this route, and the response wrapper id is identical, so the parsing
    // below is unchanged. type=product: the drawer only shows product cards.
    // routes.search_url is not published to window.routes (theme.liquid:563);
    // Shopify.routes.root is the locale-aware prefix the theme already uses.
    //
    // TWO QUERY FORMS, because neither one alone finds everything (all probed
    // on this store 2026-07-26):
    //   "drag"     -> 0    "drag*"     -> 47   (no prefix matching without *)
    //   "hallow"   -> 0    "hallow*"   -> 26
    //   "northern" -> 2    "northern*" -> 0    (!)
    // The trailing wildcard buys prefix matching — nothing appears until a
    // whole word is typed without it — but it also NARROWS the fields the
    // engine looks at: "northern" only appears in two product descriptions,
    // and the wildcard form cannot see descriptions at all. Shopify's
    // options[prefix]=last does not help (probed: still 0 for "drag"), and an
    // OR query is not supported ("northern OR northern*" -> 0).
    // So: ask with the wildcard first, and if that comes back with no product
    // cards, ask again for the literal term. The second request only happens
    // on the miss, so ordinary typing still costs one round trip.
    const searchRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const trimmedTerm = searchTerm.trim();
    // Match the results page's conservative scope rule: wildcard-first only
    // for a plain single word. Multi-word natural-language searches keep
    // Shopify's semantic behavior, and explicit field / wildcard / quoted
    // syntax is passed through untouched.
    const useWildcard = trimmedTerm && !/[\s*":]/.test(trimmedTerm);

    const fetchMarkup = (term) =>
      fetch(`${searchRoot}search?q=${encodeURIComponent(term)}&type=product&section_id=predictive-search`, {
        signal: abortController.signal,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Predictive search failed: HTTP ${response.status}`);
          }

          return response.text();
        })
        .then((text) => {
          const section = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search');
          if (!section) throw new Error('Predictive search response is missing its section wrapper');
          return section.innerHTML;
        });

    // the card class is emitted once per product card by
    // snippets/predictive-product-card.liquid, so its absence means the render
    // produced no products — whether the engine matched none or the
    // reveal-gate removed them all.
    const hasProductCards = (markup) => markup.indexOf('predictive-search__item--link-with-thumbnail') !== -1;

    fetchMarkup(useWildcard ? `${trimmedTerm}*` : trimmedTerm)
      .then((markup) => {
        if (!useWildcard || hasProductCards(markup)) return markup;
        // literal-term retry: keep the wildcard markup if it finds nothing
        // either, so the "no results" live region and the See-all button still
        // reflect the term the shopper typed.
        return fetchMarkup(trimmedTerm).then((literalMarkup) =>
          hasProductCards(literalMarkup) ? literalMarkup : markup
        );
      })
      .then((resultsMarkup) => {
        if (!isCurrentRequest()) return;
        this.abortController = null;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20 || !isCurrentRequest()) return;
        this.abortController = null;
        this.showSearchError();
        console.error(error);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (this.liveRegionTimer) clearTimeout(this.liveRegionTimer);
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    this.liveRegionTimer = setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
      this.liveRegionTimer = null;
    }, 1000);
  }

  hideSearchError() {
    const errorElement = this.querySelector('[data-predictive-search-error]');
    if (errorElement) errorElement.hidden = true;
  }

  showSearchError() {
    this.close();
    this.removeAttribute('results');
    this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    const errorElement = this.querySelector('[data-predictive-search-error]');
    if (errorElement) errorElement.hidden = false;
  }

  renderSearchResults(resultsMarkup) {
    this.hideSearchError();
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    if (this.hasAttribute('data-results-section')) this.dispatchEvent(new Event('search-storybook:results', { bubbles: true }));
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (this.hasAttribute('data-results-section')) {
      this.storybookDismissed = true;
      this.cancelPendingSearch();
      if (this.liveRegionTimer) clearTimeout(this.liveRegionTimer);
      this.clearStorybookSelection();
    }
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.hideSearchError();
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }

  clearStorybookSelection() {
    this.querySelectorAll('[aria-selected="true"]').forEach((option) => option.setAttribute('aria-selected', 'false'));
    this.input.removeAttribute('aria-activedescendant');
  }

  switchStorybookOption(step) {
    if (!this.hasAttribute('open') || this.getQuery() !== this.storybookRenderedTerm) return;
    const options = [...this.querySelectorAll('#predictive-search-results [role="option"]')].filter((option) => option.getClientRects().length && !option.closest('[hidden]'));
    if (!options.length) return;
    const selected = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    // Nothing selected yet: down starts at the first option, up at the last.
    const next = selected < 0 ? (step > 0 ? 0 : options.length - 1) : (selected + step + options.length) % options.length;
    options.forEach((option, index) => option.setAttribute('aria-selected', String(index === next)));
    this.input.setAttribute('aria-activedescendant', options[next].id);
    options[next].scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (this.statusElement) this.statusElement.textContent = '';
  }

  invalidateStorybook() {
    this.close(false);
    this.cachedResults = {};
    this.storybookRenderedTerm = '';
    this.removeAttribute('results');
    this.predictiveSearchResults.replaceChildren();
  }

  getStorybookResults(searchTerm) {
    if (this.storybookDismissed || !this.isConnected) return;
    const section = this.dataset.resultsSection;
    if (this.storybookSection !== section) {
      this.cachedResults = {};
      this.storybookSection = section;
    }
    this.cancelPendingSearch();
    const requestId = this.requestId;
    const current = () => this.isConnected && !this.storybookDismissed && requestId === this.requestId && this.dataset.resultsSection === section && searchTerm === this.getQuery();
    const key = section + ':' + searchTerm.trim().toLowerCase();
    this.hideSearchError();
    this.setLiveRegionLoadingState();
    const cached = this.cachedResults[key];
    if (cached && (!cached.boundary || Date.now() < cached.boundary)) {
      if (current()) {
        this.storybookRenderedTerm = searchTerm;
        this.renderSearchResults(cached.markup);
      }
      return;
    }
    const controller = new AbortController();
    this.abortController = controller;
    const q = searchTerm.trim();
    const plain = q && !/[\s*":]/.test(q) && !q.startsWith('-');
    const url = new URL((window.Shopify?.routes?.root || '/') + 'search', location.origin);
    url.searchParams.set('q', plain ? q + '*' : q);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('section_id', section);
    if (plain) url.searchParams.set('dm_scope', '1');
    fetch(url, { signal: controller.signal, cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error('Search section unavailable');
        return response.text();
      })
      .then((text) => {
        if (!current()) return;
        const wrapper = new DOMParser().parseFromString(text, 'text/html').getElementById('shopify-section-' + section);
        if (!wrapper?.querySelector('#predictive-search-results-groups-wrapper')) throw new Error('Search section missing');
        const boundary = Number(wrapper.querySelector('[data-reveal-boundary]')?.dataset.revealBoundary) || 0;
        this.cachedResults[key] = { markup: wrapper.innerHTML, boundary };
        const keys = Object.keys(this.cachedResults);
        if (keys.length > 30) delete this.cachedResults[keys[0]];
        this.abortController = null;
        this.storybookRenderedTerm = searchTerm;
        this.renderSearchResults(wrapper.innerHTML);
      })
      .catch((error) => {
        if (error.name === 'AbortError' || !current()) return;
        this.abortController = null;
        this.showSearchError();
      });
  }

  disconnectedCallback() {
    if (this.hasAttribute('data-results-section')) {
      this.cancelPendingSearch();
      this.cachedResults = {};
      this.storybookDismissed = true;
      if (this.liveRegionTimer) clearTimeout(this.liveRegionTimer);
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
